import { Filter, GlProgram, GpuProgram, Matrix, UniformGroup, type Sprite, type FilterSystem, type Texture, type RenderSurface } from 'pixi.js';
import { REVEAL_INNER_ALPHA, REVEAL_FEATHER, type HeroRevealEllipse } from './hero-reveal-geometry';
const vertex = `
in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vHeroUv;
uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;
uniform mat3 uHeroMatrix;
void main() {
  vec2 position=aPosition*uOutputFrame.zw+uOutputFrame.xy;
  position.x=position.x*(2.0/uOutputTexture.x)-1.0;
  position.y=position.y*(2.0*uOutputTexture.z/uOutputTexture.y)-uOutputTexture.z;
  gl_Position=vec4(position,0.0,1.0);
  vTextureCoord=aPosition*(uOutputFrame.zw*uInputSize.zw);
  vHeroUv=(uHeroMatrix*vec3(vTextureCoord,1.0)).xy;
}`;
const fragment = `
in vec2 vTextureCoord;
in vec2 vHeroUv;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uEllipse;
uniform vec2 uFade;
void main() {
  float radius=length((vHeroUv-uEllipse.xy)/uEllipse.zw);
  float opacity=mix(uFade.x,1.0,smoothstep(1.0-uFade.y,1.0,radius));
  finalColor=texture(uTexture,vTextureCoord)*opacity;
}`;
const gpuSource = `
struct GlobalFilterUniforms {
  uInputSize:vec4<f32>,uInputPixel:vec4<f32>,uInputClamp:vec4<f32>,
  uOutputFrame:vec4<f32>,uGlobalFrame:vec4<f32>,uOutputTexture:vec4<f32>,
};
struct RevealUniforms { uHeroMatrix:mat3x3<f32>,uEllipse:vec4<f32>,uFade:vec2<f32>, };
@group(0) @binding(0) var<uniform> gfu:GlobalFilterUniforms;
@group(0) @binding(1) var uTexture:texture_2d<f32>;
@group(0) @binding(2) var uSampler:sampler;
@group(1) @binding(0) var<uniform> revealUniforms:RevealUniforms;
struct VSOutput { @builtin(position) position:vec4<f32>,@location(0) uv:vec2<f32>,@location(1) heroUv:vec2<f32>, };
@vertex fn mainVertex(@location(0) aPosition:vec2<f32>) -> VSOutput {
  var p=aPosition*gfu.uOutputFrame.zw+gfu.uOutputFrame.xy;
  p.x=p.x*(2.0/gfu.uOutputTexture.x)-1.0;
  p.y=p.y*(2.0*gfu.uOutputTexture.z/gfu.uOutputTexture.y)-gfu.uOutputTexture.z;
  let uv=aPosition*(gfu.uOutputFrame.zw*gfu.uInputSize.zw);
  return VSOutput(vec4(p,0.0,1.0),uv,(revealUniforms.uHeroMatrix*vec3(uv,1.0)).xy);
}
@fragment fn mainFragment(@location(0) uv:vec2<f32>,@location(1) heroUv:vec2<f32>) -> @location(0) vec4<f32> {
  let radius=length((heroUv-revealUniforms.uEllipse.xy)/revealUniforms.uEllipse.zw);
  let opacity=mix(revealUniforms.uFade.x,1.0,smoothstep(1.0-revealUniforms.uFade.y,1.0,radius));
  return textureSample(uTexture,uSampler,uv)*opacity;
}`;
/** Applied to one occluder, never to the hero, its parent, or the scene. */
export class HeroRevealFilter extends Filter {
  private hero: Sprite;
  private readonly revealUniforms: UniformGroup;
  constructor(hero: Sprite) {
    const revealUniforms = new UniformGroup({
      uHeroMatrix: { value: new Matrix(), type: 'mat3x3<f32>' },
      uEllipse: { value: new Float32Array([.5, .5, .5, .5]), type: 'vec4<f32>' },
      uFade: { value: new Float32Array([REVEAL_INNER_ALPHA, REVEAL_FEATHER]), type: 'vec2<f32>' },
    });
    super({ glProgram: GlProgram.from({ vertex, fragment, name: 'own-hero-local-reveal' }),
      gpuProgram: GpuProgram.from({ vertex: { source: gpuSource, entryPoint: 'mainVertex' }, fragment: { source: gpuSource, entryPoint: 'mainFragment' } }),
      resources: { revealUniforms }, padding: 0, resolution: 'inherit' });
    this.hero = hero;
    this.revealUniforms = revealUniforms;
  }
  setHero(hero: Sprite, ellipse: HeroRevealEllipse): void {
    this.hero = hero;
    const values = this.revealUniforms.uniforms.uEllipse as Float32Array;
    values[0] = ellipse.x;
    values[1] = ellipse.y;
    values[2] = ellipse.radiusX;
    values[3] = ellipse.radiusY;
  }
  override apply(manager: FilterSystem, input: Texture, output: RenderSurface, clear: boolean): void {
    // Pixi's public mask/displacement mapping includes camera, nested render targets,
    // mirrored scale and the current texture/anchor, unlike screen-frame guesses.
    manager.calculateSpriteMatrix(this.revealUniforms.uniforms.uHeroMatrix as Matrix, this.hero);
    manager.applyFilter(this, input, output, clear);
  }
}
